// components/AddressAutocomplete.jsx
import React, { useEffect, useRef, useState } from "react";
import keycloak from "../APIs/keycloak";

const BACKEND_URL = process.env.REACT_APP_API_BASE_URL;

// ✅ Add this helper at the top (outside the component)
const STATE_NAME_TO_CODE = {
  alabama: "AL",
  alaska: "AK",
  arizona: "AZ",
  arkansas: "AR",
  california: "CA",
  colorado: "CO",
  connecticut: "CT",
  delaware: "DE",
  florida: "FL",
  georgia: "GA",
  hawaii: "HI",
  idaho: "ID",
  illinois: "IL",
  indiana: "IN",
  iowa: "IA",
  kansas: "KS",
  kentucky: "KY",
  louisiana: "LA",
  maine: "ME",
  maryland: "MD",
  massachusetts: "MA",
  michigan: "MI",
  minnesota: "MN",
  mississippi: "MS",
  missouri: "MO",
  montana: "MT",
  nebraska: "NE",
  nevada: "NV",
  "new hampshire": "NH",
  "new jersey": "NJ",
  "new mexico": "NM",
  "new york": "NY",
  "north carolina": "NC",
  "north dakota": "ND",
  ohio: "OH",
  oklahoma: "OK",
  oregon: "OR",
  pennsylvania: "PA",
  "rhode island": "RI",
  "south carolina": "SC",
  "south dakota": "SD",
  tennessee: "TN",
  texas: "TX",
  utah: "UT",
  vermont: "VT",
  virginia: "VA",
  washington: "WA",
  "west virginia": "WV",
  wisconsin: "WI",
  wyoming: "WY",
};

const normalizeState = (state) => {
  if (!state) return state;
  if (state.length === 2) return state.toUpperCase();
  return STATE_NAME_TO_CODE[state.toLowerCase()] || state;
};

const getAuthHeaders = async () => {
  if (keycloak?.authenticated) {
    await keycloak.updateToken(30);
  }
  return keycloak?.token ? { Authorization: `Bearer ${keycloak.token}` } : {};
};

export default function AddressAutocomplete({
  value,
  onSelect,
  placeholder = "Start typing an address…",
}) {
  const [query, setQuery] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const rootRef = useRef(null);
  const acRef = useRef(null);

  useEffect(() => {
    setQuery(value || "");
  }, [value]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    if (!query?.trim()) {
      setOptions([]);
      return;
    }
    if (acRef.current) acRef.current.abort();
    const ctrl = new AbortController();
    acRef.current = ctrl;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const url = new URL("/api/v1/geo/autocomplete", BACKEND_URL);
        url.searchParams.set("q", query.trim());
        const res = await fetch(url, {
          signal: ctrl.signal,
          headers: await getAuthHeaders(),
        });
        if (res.ok) {
          const data = await res.json();
          setOptions(data);
        }
      } catch (err) {
        console.error("Fetch error:", err);
      }
      setLoading(false);
    }, 250);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [query]);

  // ✅ REPLACE this entire pick function
  const pick = async (opt) => {
    const displayText = `${opt.primaryText}${opt.secondaryText ? ", " + opt.secondaryText : ""}`;
    setQuery(displayText);
    setOpen(false);

    try {
      const url = new URL("/api/v1/geo/geocode", BACKEND_URL);
      url.searchParams.set(
        "address",
        `${opt.primaryText} ${opt.secondaryText ?? ""}`,
      );
      const res = await fetch(url, {
        headers: await getAuthHeaders(),
      });
      if (!res.ok) return;

      const addr = await res.json();

      // Trim addressLine to only the street portion (before comma)
      let streetOnly = addr.addressLine;
      if (streetOnly) {
        streetOnly = streetOnly.split(",")[0].trim();
      }

      // ✅ Normalize state to 2-letter code
      const normalized = {
        addressLine: streetOnly,
        city: addr.city,
        state: normalizeState(addr.state), // ✅ NEW: Normalize state here
        postalCode: addr.postalCode || addr.zipCode,
        country: addr.country || "United States",
      };

      onSelect?.(normalized);
    } catch (err) {
      console.error("Geocode error:", err);
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <input
        className="w-full border rounded p-2"
        placeholder={placeholder}
        value={query}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
      />
      {open && (
        <div className="absolute z-10 mt-1 w-full max-h-60 overflow-auto rounded border bg-white shadow">
          {loading ? (
            <div className="px-3 py-2 text-sm text-gray-500">Searching…</div>
          ) : options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">No matches</div>
          ) : (
            <ul>
              {options.map((o) => (
                <li
                  key={o.placeId}
                  className="cursor-pointer px-3 py-2 hover:bg-gray-50"
                  onClick={() => pick(o)}
                >
                  <div className="text-sm font-medium">{o.primaryText}</div>
                  <div className="text-xs text-gray-500">{o.secondaryText}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
